import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Send, Image as ImageIcon, X, Loader2, CheckCircle, Mail, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';
import { useAuthStore } from '../store';

const API_URL = process.env.REACT_APP_BACKEND_URL || (typeof window !== 'undefined' ? ((window.location.hostname === 'devapp.soviewfx.fr' || window.location.hostname === 'devadmin.soviewfx.fr') ? 'https://devhub.soviewfx.fr' : ((window.location.hostname === 'app.soviewfx.fr' || window.location.hostname === 'admin.soviewfx.fr') ? 'https://hub.soviewfx.fr' : '')) : '');

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function SendSimulationPage() {
  const navigate = useNavigate();
  const { user, token } = useAuthStore();
  const fileInputRef = useRef(null);

  const [image, setImage] = useState(null);
  const [imageBase64, setImageBase64] = useState(null);
  const [projectName, setProjectName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [sentEmail, setSentEmail] = useState('');
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!user) { navigate('/home'); return; }
  }, [user]);

  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Veuillez sélectionner une image');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setImage(URL.createObjectURL(file));
      setImageBase64(reader.result.split(',')[1]);
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const removeImage = () => {
    if (image) URL.revokeObjectURL(image);
    setImage(null);
    setImageBase64(null);
  };

  const validate = () => {
    const errs = {};
    if (!email) errs.email = 'L\'email est requis';
    else if (!EMAIL_REGEX.test(email)) errs.email = 'Email invalide';
    if (!imageBase64) errs.image = 'L\'image est requise';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSend = async () => {
    if (!validate()) return;
    setSending(true);
    try {
      const res = await fetch(`${API_URL}/api/shares/send-simulation`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email,
          project_name: projectName || undefined,
          message: message || undefined,
          image_base64: imageBase64
        })
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.detail || 'Envoi échoué');
      }
      setSentEmail(email);
      setSent(true);
      toast.success(`Simulation envoyée à ${email} !`);
    } catch (err) {
      toast.error(err.message || 'Erreur lors de l\'envoi');
    } finally {
      setSending(false);
    }
  };

  const handleReset = () => {
    removeImage();
    setProjectName('');
    setEmail('');
    setMessage('');
    setSent(false);
    setSentEmail('');
    setErrors({});
  };

  return (
    <div className="h-full flex flex-col bg-background">
      {/* Header */}
      <header className="h-14 glass flex items-center px-4 gap-4 flex-shrink-0">
        <button onClick={() => navigate('/home')} className="p-2 rounded-lg hover:bg-white/5">
          <ArrowLeft size={20} />
        </button>
        <h1 className="font-display font-bold text-lg uppercase tracking-wide">
          Envoyer la simulation
        </h1>
      </header>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-5 pb-safe">
        {/* Sent confirmation */}
        {sent ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="card p-8 text-center"
          >
            <CheckCircle size={64} className="text-green-500 mx-auto mb-4" />
            <h2 className="font-display font-bold text-xl text-white mb-2">Simulation envoyée !</h2>
            <p className="text-muted-foreground mb-6">
              Un email a été envoyé à <span className="text-primary font-semibold">{sentEmail}</span>
            </p>
            <div className="flex gap-3 justify-center">
              <button onClick={handleReset} className="btn-secondary">
                Nouvel envoi
              </button>
              <button onClick={() => navigate('/home')} className="btn-primary">
                Retour à l'accueil
              </button>
            </div>
          </motion.div>
        ) : (
          <>
            {/* Image Upload */}
            <div>
              <label className="block text-sm font-medium text-white mb-2">
                Image de la simulation <span className="text-red-400">*</span>
              </label>
              {image ? (
                <div className="relative">
                  <img
                    src={image}
                    alt="Simulation"
                    className="w-full h-48 object-cover rounded-2xl border border-white/10"
                  />
                  <button
                    onClick={removeImage}
                    className="absolute top-2 right-2 p-1.5 rounded-full bg-black/60 text-white hover:bg-black/80"
                  >
                    <X size={16} />
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className={`w-full h-32 rounded-2xl border-2 border-dashed flex flex-col items-center justify-center gap-2 transition-all ${
                    errors.image ? 'border-red-400 bg-red-500/5' : 'border-white/10 bg-white/5 hover:border-primary/50 hover:bg-white/10'
                  }`}
                >
                  <ImageIcon size={32} className={errors.image ? 'text-red-400' : 'text-muted-foreground'} />
                  <p className={`text-sm ${errors.image ? 'text-red-400' : 'text-muted-foreground'}`}>
                    {errors.image || 'Cliquez pour ajouter une image'}
                  </p>
                </button>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleImageSelect}
              />
            </div>

            {/* Project Name */}
            <div>
              <label className="block text-sm font-medium text-white mb-2">
                Nom du projet simulé
              </label>
              <input
                type="text"
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
                className="input"
                placeholder="ex: Store vitrine 120cm"
              />
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-white mb-2">
                Email du destinataire <span className="text-red-400">*</span>
              </label>
              <div className="relative">
                <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setErrors(e => ({ ...e, email: '' })); }}
                  className={`input pl-10 ${errors.email ? 'border-red-400' : ''}`}
                  placeholder="client@exemple.fr"
                />
              </div>
              {errors.email && (
                <p className="text-xs text-red-400 mt-1">{errors.email}</p>
              )}
            </div>

            {/* Message */}
            <div>
              <label className="block text-sm font-medium text-white mb-2">
                Message optionnel
              </label>
              <div className="relative">
                <MessageSquare size={16} className="absolute left-3 top-3 text-muted-foreground" />
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  className="input pl-10 min-h-[100px] resize-none pt-3"
                  placeholder="Bonjour, voici la simulation de votre futur store..."
                  maxLength={1000}
                />
              </div>
              <p className="text-xs text-muted-foreground text-right mt-1">{message.length}/1000</p>
            </div>

            {/* Send Button */}
            <button
              onClick={handleSend}
              disabled={sending}
              className="btn-primary w-full flex items-center justify-center gap-2 py-4"
            >
              {sending ? (
                <Loader2 size={20} className="animate-spin" />
              ) : (
                <Send size={20} />
              )}
              {sending ? 'Envoi en cours...' : 'Envoyer la simulation'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
